import { ArrowDownRight, ArrowUpRight } from "lucide-react";
import { BOOKING_URL } from "@/lib/site";
import { Reveal } from "../Reveal";
import { PHOTO_SIZES, PHOTOS, type Photo } from "../images";

type Category = {
  id: string;
  title: string;
  body: string;
  highlights: string[];
  photo: Photo;
  sizes: string;
  span: string;
};

const CATEGORIES: Category[] = [
  {
    id: "rv-sites",
    title: "RV Sites",
    body: "Spacious pull-through and back-in sites with full hookups and 30- or 50-amp service.",
    highlights: ["Full Hookups", "30 & 50 Amp", "Pull-Through & Back-In"],
    photo: PHOTOS.rvSite,
    sizes: PHOTO_SIZES.gridWide,
    span: "lg:col-span-7",
  },
  {
    id: "cabins",
    title: "Cabins",
    body: "Furnished cabin options for guests who want additional space and convenient in-room amenities.",
    highlights: ["Furnished", "Kitchen Essentials", "Private Bath"],
    photo: PHOTOS.parkModelPatio,
    sizes: PHOTO_SIZES.gridNarrow,
    span: "lg:col-span-5",
  },
  {
    id: "motel-rooms",
    title: "Motel and Efficiency Rooms",
    body: "Practical furnished accommodations for fishing trips, work assignments, weekend visits, and extended stays.",
    highlights: ["Furnished Rooms", "Kitchen Conveniences", "Wi-Fi"],
    photo: PHOTOS.cabinRow,
    sizes: PHOTO_SIZES.half,
    span: "lg:col-span-6",
  },
  {
    id: "tent-camping",
    title: "Tent Camping",
    body: "A relaxed camping option for guests who want to enjoy the South Texas outdoors while remaining close to campground conveniences.",
    highlights: ["Outdoor Stay", "Near Amenities", "Relaxed Setting"],
    photo: PHOTOS.coveredParking,
    sizes: PHOTO_SIZES.half,
    span: "lg:col-span-6",
  },
];

export function AccommodationDirectory() {
  return (
    <section
      className="bg-background py-24 sm:py-28 lg:py-32"
      aria-labelledby="directory-title"
      id="stay-options"
    >
      <div className="mx-auto max-w-[1400px] px-4 sm:px-6 lg:px-10">
        <Reveal className="max-w-2xl">
          <p className="eyebrow text-clay">Accommodations</p>
          <h2
            id="directory-title"
            className="mt-4 font-display text-3xl leading-tight sm:text-4xl lg:text-[2.9rem]"
          >
            Choose How You Want to Stay
          </h2>
          <p className="mt-6 text-base leading-relaxed text-muted-foreground sm:text-lg">
            From spacious RV sites to furnished rooms and cabins, Amigo Inn offers flexible options
            for weekend trips, seasonal visits, work assignments, and extended stays.
          </p>
        </Reveal>

        <ul className="mt-14 grid gap-x-8 gap-y-14 lg:grid-cols-12">
          {CATEGORIES.map((category, i) => (
            <li key={category.id} className={category.span}>
              <Reveal delay={(i % 2) * 0.08} className="group h-full">
                <article className="flex h-full flex-col">
                  <a
                    href={`#${category.id}`}
                    className="media-zoom block rounded-sm"
                    tabIndex={-1}
                    aria-hidden="true"
                  >
                    <img
                      src={category.photo.src}
                      srcSet={category.photo.srcSet}
                      sizes={category.sizes}
                      alt={category.photo.alt}
                      width={category.photo.width}
                      height={category.photo.height}
                      loading="lazy"
                      decoding="async"
                      className="aspect-[16/10] w-full rounded-sm object-cover"
                    />
                  </a>

                  <h3 className="mt-6 font-display text-2xl text-forest-deep">
                    <a href={`#${category.id}`} className="link-underline">
                      {category.title}
                    </a>
                  </h3>
                  <p className="mt-3 flex-1 text-sm leading-relaxed text-muted-foreground">
                    {category.body}
                  </p>

                  <ul className="mt-4 flex flex-wrap gap-2">
                    {category.highlights.map((item) => (
                      <li
                        key={item}
                        className="rounded-full border border-forest/15 bg-secondary px-3 py-1 text-xs font-semibold text-forest-deep"
                      >
                        {item}
                      </li>
                    ))}
                  </ul>

                  <div className="mt-5 flex flex-wrap items-center gap-x-6 gap-y-3">
                    <a
                      href={BOOKING_URL}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link-underline text-sm font-semibold text-clay"
                    >
                      Check Availability <ArrowUpRight className="size-4" aria-hidden="true" />
                    </a>
                    <a
                      href={`#${category.id}`}
                      className="link-underline text-sm font-semibold text-forest"
                    >
                      View Details <ArrowDownRight className="size-4" aria-hidden="true" />
                    </a>
                  </div>
                </article>
              </Reveal>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );
}
